import React, { useEffect, useState, useRef, useMemo } from 'react'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { getHubReturn, hasInAppHistory } from 'utils/hubReturnPath'
import styled, { keyframes } from 'styled-components'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Tooltip from '@mui/material/Tooltip'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import RepeatOutlinedIcon from '@mui/icons-material/RepeatOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Checkbox from '@mui/material/Checkbox'
import {
  createTaskRoutine,
  getFormTemplatesRoutine,
  getProjectsRoutine,
  getTeamsRoutine
} from './ducks/actions'
import { maskEmail } from 'utils/string'
import {
  selectFormTemplates,
  selectProjects,
  selectTasks,
  selectTeams,
  selectCreateTaskLoading
} from './ducks/selectors'
import { fetchUserBps, fetchBp } from 'services/BpService'
import { fetchBpUnits } from 'services/unitsService'
import { createProject as createProjectApi } from 'services/taskService'
import { listContractors } from 'services/contractorService'
import { fetchTaskDomains, fetchProgramme } from 'services/ProgrammeService'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import UserPicker from './components/UserPicker'
import FormTemplateField from './components/FormTemplateField'
import SearchableSelect from 'components/SearchableSelect'
import { getFileTypesRoutine } from 'ducks/files/actions'
import { getFileTypes, selectIsLoadingFileTypes } from 'ducks/files/selectors'
import { PRIORITY_META } from 'features/taskManagement/taskPriority'

const priorityOptions = [
  { value: '', label: 'None' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: PRIORITY_META.critical.label }
]

const taskTypeOptions = [
  { value: '', label: 'Unspecified' },
  { value: 'instructional', label: 'Instructional' },
  { value: 'data_collection', label: 'Data collection' },
  { value: 'conditional', label: 'Conditional' },
]

const taskActionOptions = [
  {
    value: 'form',
    label: 'Fill in form',
    description: 'The assignee completes a selected form template.',
    icon: DescriptionOutlinedIcon
  },
  {
    value: 'upload_single',
    label: 'Upload file',
    description: 'The assignee uploads one required document type.',
    icon: UploadFileOutlinedIcon
  },
  {
    value: 'upload_multiple',
    label: 'Upload multiple files',
    description: 'The assignee uploads several files.',
    icon: FileCopyOutlinedIcon
  }
]

const splitParamList = value =>
  (value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)

const parseJsonParam = value => {
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const recurrenceUnitOptions = [
  { value: 'week', label: 'Week(s)' },
  { value: 'month', label: 'Month(s)' },
  { value: 'year', label: 'Year(s)' }
]

const snapStartToWeekday = (currentStart, todayLocal, dow) => {
  const base = moment(currentStart || todayLocal)
  const start = base.isBefore(moment(todayLocal), 'day') ? moment(todayLocal) : base
  const diff = (dow - start.day() + 7) % 7
  return start.clone().add(diff, 'days').format('YYYY-MM-DD')
}

const snapStartToDayOfMonth = (currentStart, todayLocal, dom) => {
  const base = moment(currentStart || todayLocal)
  const start = base.isBefore(moment(todayLocal), 'day') ? moment(todayLocal) : base
  let d = start.clone().date(Math.min(dom, start.daysInMonth()))
  if (d.isBefore(moment(todayLocal), 'day')) {
    d = start.clone().add(1, 'month')
    d = d.date(Math.min(dom, d.daysInMonth()))
  }
  return d.format('YYYY-MM-DD')
}

const CreateTask = ({ embedded = false, initialOverrides = null, onCreated, onCancel } = {}) => {
  const todayLocal = useMemo(() => {
    const n = new Date()
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`
  }, [])
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const routerNavigate = useNavigate()
  const prefill = useMemo(() => ({
    title: searchParams.get('title') || '',
    description: searchParams.get('description') || '',
    bpId: searchParams.get('bpId') || '',
    bpName: searchParams.get('bpName') || '',
    source: searchParams.get('source') || '',
    programmeObligationId: searchParams.get('programmeObligationId') || '',
    taskDomainId: searchParams.get('taskDomainId') || searchParams.get('domainId') || '',
    priority: searchParams.get('priority') || '',
    deadline: searchParams.get('deadline') || '',
    recurrenceType: searchParams.get('recurrenceType') || '',
    recurrenceInterval: searchParams.get('recurrenceInterval') || '',
    recurrenceUnit: searchParams.get('recurrenceUnit') || '',
    recurrenceStartDate: searchParams.get('recurrenceStartDate') || '',
    unitId: searchParams.get('unitId') || searchParams.get('unit') || '',
    unitName: searchParams.get('unitName') || '',
    unitIds: splitParamList(searchParams.get('unitIds') || searchParams.get('units') || ''),
    unitMode: searchParams.get('unitMode') || '',
    assigneeIds: splitParamList(searchParams.get('assigneeIds') || ''),
    assigneeUsers: parseJsonParam(searchParams.get('assigneeUsers') || ''),
    checklist: searchParams.get('checklist') === '1',
    checklistTemplateId: searchParams.get('templateId') || '',
    checklistTemplateName: searchParams.get('templateName') || '',
    checklistItemId: searchParams.get('itemId') || '',
    checklistItemTitle: searchParams.get('itemTitle') || '',
    checklistGroupNo: searchParams.get('groupNo') || '',
    checklistAssignmentScope: searchParams.get('assignmentScope') || '',
    checklistFileTypeId: searchParams.get('fileTypeId') || '',
    checklistFileCategoryId: searchParams.get('fileCategoryId') || '',
    checklistTagIds: splitParamList(searchParams.get('tagIds') || ''),
    followUpFor: searchParams.get('followUpFor') || '',
    followUpTitle: searchParams.get('followUpTitle') || '',
    sourceFileId: searchParams.get('sourceFileId') || '',
    sourceFileName: searchParams.get('sourceFileName') || '',
    openRemedials: Number(searchParams.get('openRemedials')) || 0,
    ...(initialOverrides || {}),
  }), [searchParams, initialOverrides])
  const appliedPrefillRef = useRef({
    basics: false,
    bp: false,
    unit: false,
    template: false,
    assignees: false,
    domain: false
  })
  const formTemplates = useSelector(selectFormTemplates)
  const fileTypes = useSelector(getFileTypes) || []
  const fileTypesLoading = useSelector(selectIsLoadingFileTypes)
  const projects = useSelector(selectProjects)
  const teams = useSelector(selectTeams)
  const tasks = useSelector(selectTasks)

  const [blockOriginal, setBlockOriginal] = useState(false)
  const [bps, setBps] = useState([])
  const [bpsLoading, setBpsLoading] = useState(false)
  const [bpsLoaded, setBpsLoaded] = useState(false)
  const [units, setUnits] = useState([])
  const [unitsLoading, setUnitsLoading] = useState(false)

  const submitting = useSelector(selectCreateTaskLoading)
  const submittedRef = useRef(false)
  const prevTasksLengthRef = useRef(tasks.length)

  const [multiUnitMode, setMultiUnitMode] = useState(false)
  const [showCreateProject, setShowCreateProject] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [creatingProject, setCreatingProject] = useState(false)
  const [createProjectError, setCreateProjectError] = useState('')

  const [availableContractors, setAvailableContractors] = useState([])
  useEffect(() => {
    listContractors()
      .then(res => setAvailableContractors(res?.data?.data?.contractors || []))
      .catch(() => {})
  }, [])

  const [availableDomains, setAvailableDomains] = useState([])
  useEffect(() => {
    fetchTaskDomains()
      .then(res => setAvailableDomains((res?.data?.data || []).filter(d => d.isActive)))
      .catch(() => {})
  }, [])

  const [formValues, setFormValues] = useState({
    title: '',
    taskActionType: prefill.checklist ? 'upload_single' : 'form',
    formTemplateId: '',
    fileTypeIds: prefill.checklistFileTypeId
      ? [String(prefill.checklistFileTypeId)]
      : [],
    buildingPassportId: '',
    buildingUnitPassportId: '',
    unitIds: [],
    unitMode: 'shared',
    description: '',
    priority: '',
    taskType: '',
    estimatedDurationMinutes: '',
    deadline: '',
    taskProjectId: '',
    taskTeamId: '',
    contractorId: '',
    poNumber: '',
    taskDomainId: '',
    programmeObligationId: '',
    assigneeIds: [],
    inviteEmails: [],
    requiresApproval: false,
    requiresEvidence: false,
    approverIds: [],
    requiresSignOff: false,
    recurrenceType: 'one_off',
    recurrenceInterval: 1,
    recurrenceUnit: 'month',
    recurrenceStartDate: '',
    recurrenceEndDate: ''
  })
  const sourceDocumentLinked = !!prefill.sourceFileId
    && (!prefill.bpId || formValues.buildingPassportId === prefill.bpId)

  const [errors, setErrors] = useState({})

  const uploadTaskTemplate = useMemo(
    () => (formTemplates || []).find(template => template.name === 'Checklist Upload Task') || null,
    [formTemplates]
  )
  const selectedFormTemplate = useMemo(
    () => (formTemplates || []).find(
      template => String(template.id) === String(formValues.formTemplateId)
    ) || null,
    [formValues.formTemplateId, formTemplates]
  )
  const selectedFileTypeOptions = useMemo(
    () => formValues.fileTypeIds
      .map(id => fileTypes.find(type => String(type.id) === String(id)))
      .filter(Boolean),
    [fileTypes, formValues.fileTypeIds]
  )
  const orderedFileTypeOptions = useMemo(() => {
    const selectedIds = new Set(formValues.fileTypeIds.map(String))
    return [...fileTypes].sort((a, b) => {
      const aSelected = selectedIds.has(String(a.id))
      const bSelected = selectedIds.has(String(b.id))
      if (aSelected !== bSelected) return aSelected ? -1 : 1
      return String(a.name || '').localeCompare(String(b.name || ''))
    })
  }, [fileTypes, formValues.fileTypeIds])
  const orderedUnits = useMemo(() => {
    const selectedIds = new Set(formValues.unitIds.map(String))
    return [...units].sort((a, b) => {
      const aSelected = selectedIds.has(String(a.id))
      const bSelected = selectedIds.has(String(b.id))
      if (aSelected !== bSelected) return aSelected ? -1 : 1
      return String(a.name || '').localeCompare(String(b.name || ''), undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    })
  }, [formValues.unitIds, units])

  const buildingPassportOptions = useMemo(() => {
    if (
      !prefill.checklist ||
      !formValues.buildingPassportId ||
      bps.some(bp => bp.id === formValues.buildingPassportId)
    ) {
      return bps
    }

    return [
      {
        id: formValues.buildingPassportId,
        name: prefill.bpName || 'Selected Building Passport'
      },
      ...bps
    ]
  }, [
    bps,
    formValues.buildingPassportId,
    prefill.bpName,
    prefill.checklist
  ])

  const unitOptions = useMemo(() => {
    if (
      !prefill.checklist ||
      !formValues.buildingUnitPassportId ||
      units.some(unit => unit.id === formValues.buildingUnitPassportId)
    ) {
      return units
    }

    return [
      {
        id: formValues.buildingUnitPassportId,
        name: prefill.unitName || 'Selected unit'
      },
      ...units
    ]
  }, [
    formValues.buildingUnitPassportId,
    prefill.checklist,
    prefill.unitName,
    units
  ])

  const shouldShowUnitPicker = Boolean(formValues.buildingPassportId) && (
    unitsLoading ||
    unitOptions.length > 0 ||
    (prefill.checklist && Boolean(prefill.unitId || prefill.unitIds.length > 0))
  )

  const selectedTeam = useMemo(() => {
    if (!formValues.taskTeamId) return null
    return teams.find(t => t.id === formValues.taskTeamId) || null
  }, [teams, formValues.taskTeamId])

  const internalMembers = useMemo(() => {
    if (!selectedTeam?.members) return []
    return selectedTeam.members.filter(m => m.user && !m.isPending)
  }, [selectedTeam])

  const externalMembers = useMemo(() => {
    if (!selectedTeam?.members) return []
    return selectedTeam.members.filter(m => !m.user || m.isPending)
  }, [selectedTeam])

  const handleAddSingleInternalAssignee = userId => {
    if (!formValues.assigneeIds.includes(userId)) {
      handleChange('assigneeIds', [...formValues.assigneeIds, userId])
    }
  }

  const handleAddInternalAsAssignees = () => {
    const currentIds = formValues.assigneeIds
    const newIds = internalMembers
      .map(m => m.user.id)
      .filter(id => !currentIds.includes(id))
    if (newIds.length > 0) {
      handleChange('assigneeIds', [...currentIds, ...newIds])
    }
  }

  const handleAddExternalAsInvites = () => {
    const currentEmails = formValues.inviteEmails
    const currentEmailStrings = currentEmails.map(item =>
      typeof item === 'string' ? item : item.email
    )
    const newInvites = externalMembers
      .filter(m => m.email && !currentEmailStrings.includes(m.email))
      .map(m => ({ email: m.email, name: m.name || null }))
    if (newInvites.length > 0) {
      handleChange('inviteEmails', [...currentEmails, ...newInvites])
    }
  }

  const handleAddSingleExternalInvite = (email, name = null) => {
    const alreadyAdded = formValues.inviteEmails.some(item =>
      typeof item === 'string' ? item === email : item.email === email
    )
    if (email && !alreadyAdded) {
      handleChange('inviteEmails', [
        ...formValues.inviteEmails,
        { email, name: name || null }
      ])
    }
  }

  const handleCreateProject = async () => {
    if (!newProjectName.trim() || creatingProject) return
    setCreateProjectError('')
    if (!formValues.buildingPassportId) {
      setCreateProjectError('Please select a Building Passport first')
      return
    }
    setCreatingProject(true)
    try {
      const res = await createProjectApi({
        buildingPassportId: formValues.buildingPassportId,
        name: newProjectName.trim()
      })
      const newProject = res?.data?.data || res?.data
      dispatch(getProjectsRoutine())
      if (newProject?.id) {
        handleChange('taskProjectId', newProject.id)
      }
      setNewProjectName('')
      setShowCreateProject(false)
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to create project'
      setCreateProjectError(msg)
    } finally {
      setCreatingProject(false)
    }
  }

  useEffect(() => {
    dispatch(getFormTemplatesRoutine({
      'filter[status]': 'published',
      includeHidden: 1
    }))
    dispatch(getFileTypesRoutine())
    dispatch(getProjectsRoutine())
    dispatch(getTeamsRoutine())
  }, [dispatch])

  // Apply title/description from URL query params on mount (e.g. audit recommendations deep-link here).
  useEffect(() => {
    if (appliedPrefillRef.current.basics) return
    const hasAny = prefill.title || prefill.description || prefill.priority ||
      prefill.deadline || prefill.recurrenceType
    if (hasAny) {
      setFormValues(prev => ({
        ...prev,
        title: prefill.title || prev.title,
        description: prefill.description || prev.description,
        priority: prefill.priority || prev.priority,
        deadline: prefill.deadline || prev.deadline,
        ...(prefill.recurrenceType === 'repeating' ? {
          recurrenceType: 'repeating',
          recurrenceInterval: Number(prefill.recurrenceInterval) || prev.recurrenceInterval,
          recurrenceUnit: prefill.recurrenceUnit || prev.recurrenceUnit,
          recurrenceStartDate: prefill.recurrenceStartDate || prev.recurrenceStartDate,
        } : {}),
      }))
      appliedPrefillRef.current.basics = true
    }
  }, [prefill])

  useEffect(() => {
    if (appliedPrefillRef.current.domain) return
    if (!prefill.taskDomainId || availableDomains.length === 0) return
    if (availableDomains.some(d => d.id === prefill.taskDomainId)) {
      setFormValues(prev => ({ ...prev, taskDomainId: prefill.taskDomainId }))
      appliedPrefillRef.current.domain = true
    }
  }, [prefill.taskDomainId, availableDomains])

  useEffect(() => {
    if (!prefill.programmeObligationId) return
    setFormValues(prev => (
      prev.programmeObligationId ? prev : { ...prev, programmeObligationId: prefill.programmeObligationId }
    ))
  }, [prefill.programmeObligationId])

  // A linked task follows the obligation's cadence (RecurrenceEngine), so it
  // can't carry its own recurrence — force one-off whenever one is selected.
  useEffect(() => {
    if (formValues.programmeObligationId && formValues.recurrenceType !== 'one_off') {
      setFormValues(prev => ({ ...prev, recurrenceType: 'one_off' }))
    }
  }, [formValues.programmeObligationId, formValues.recurrenceType])

  const [obligationOptions, setObligationOptions] = useState([])
  useEffect(() => {
    if (!formValues.buildingPassportId) {
      setObligationOptions([])
      setFormValues(prev => (prev.programmeObligationId ? { ...prev, programmeObligationId: '' } : prev))
      return
    }
    let cancelled = false
    fetchProgramme(formValues.buildingPassportId)
      .then(res => {
        if (cancelled) return
        const domains = res?.data?.data?.domains || []
        const options = []
        domains.forEach(d => {
          ;(d.obligations || []).forEach(o => {
            if (!o.isSkipped) {
              options.push({ id: o.id, name: o.name, domainName: d.name, kind: o.obligationKind })
            }
          })
        })
        setObligationOptions(options)
        setFormValues(prev => {
          const wanted = prefill.programmeObligationId
          if (wanted && !prev.programmeObligationId && options.some(o => o.id === wanted)) {
            return { ...prev, programmeObligationId: wanted }
          }
          if (prev.programmeObligationId && !options.some(o => o.id === prev.programmeObligationId)) {
            return { ...prev, programmeObligationId: '' }
          }
          return prev
        })
      })
      .catch(() => { if (!cancelled) setObligationOptions([]) })
    return () => { cancelled = true }
  }, [formValues.buildingPassportId, prefill.programmeObligationId])

  useEffect(() => {
    const loadBps = async () => {
      setBpsLoading(true)
      try {
        const result = await fetchUserBps({
          page: 1,
          sort: 'name',
          filters: '&filter[task_management_enabled]=1&limit=250'
        })
        const entries = result?.data?.entries || result?.data?.data || []
        setBps(entries)
      } catch (error) {
        console.error('Failed to load Building Passports:', error)
      } finally {
        setBpsLoading(false)
        setBpsLoaded(true)
      }
    }
    loadBps()
  }, [])

  // Apply bpId prefill once the BP list is available and contains it.
  useEffect(() => {
    if (appliedPrefillRef.current.bp) return
    if (!prefill.bpId || !bpsLoaded) return
    const match = bps.find(b => b.id === prefill.bpId)
    if (match) {
      setFormValues(prev => ({ ...prev, buildingPassportId: prefill.bpId }))
      appliedPrefillRef.current.bp = true
      return
    }
    appliedPrefillRef.current.bp = true
    let cancelled = false
    fetchBp(prefill.bpId)
      .then(res => {
        const bp = res?.data?.data || res?.data
        if (cancelled || !bp?.id) return
        setBps(prev => (prev.some(b => b.id === bp.id) ? prev : [bp, ...prev]))
        setFormValues(prev => ({ ...prev, buildingPassportId: bp.id }))
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [prefill.bpId, bps, bpsLoaded])

  // Checklist task links already carry the BP id/name, so do not wait for the async
  // BP list before selecting it. This keeps the checklist-only flow hydrated on load.
  useEffect(() => {
    if (!prefill.checklist || appliedPrefillRef.current.bp || !prefill.bpId) return

    setFormValues(prev => {
      if (prev.buildingPassportId === prefill.bpId) return prev
      return { ...prev, buildingPassportId: prefill.bpId }
    })
    appliedPrefillRef.current.bp = true
  }, [prefill.bpId, prefill.checklist])

  useEffect(() => {
    if (formValues.taskActionType === 'form' || !uploadTaskTemplate?.id) return

    setFormValues(prev => {
      if (String(prev.formTemplateId) === String(uploadTaskTemplate.id)) return prev
      return {
        ...prev,
        formTemplateId: uploadTaskTemplate.id,
        taskType: prev.taskType || 'data_collection',
        requiresApproval: false
      }
    })
    appliedPrefillRef.current.template = true
  }, [formValues.taskActionType, uploadTaskTemplate])

  useEffect(() => {
    if (appliedPrefillRef.current.template) return
    if (prefill.checklist || !prefill.programmeObligationId) return
    if (formTemplates.length === 0) return

    const defaultTemplate = formTemplates.find(
      template => template.name === 'Obligation Completion Record'
    )

    if (defaultTemplate?.id) {
      setFormValues(prev => prev.formTemplateId
        ? prev
        : { ...prev, formTemplateId: defaultTemplate.id })
    }
    appliedPrefillRef.current.template = true
  }, [formTemplates, prefill.checklist, prefill.programmeObligationId])

  useEffect(() => {
    if (appliedPrefillRef.current.assignees) return
    if (prefill.assigneeIds.length === 0) return

    setFormValues(prev => ({
      ...prev,
      assigneeIds: Array.from(new Set([...prev.assigneeIds, ...prefill.assigneeIds]))
    }))
    appliedPrefillRef.current.assignees = true
  }, [prefill.assigneeIds])

  useEffect(() => {
    if (!formValues.buildingPassportId) {
      setUnits([])
      return
    }
    const loadUnits = async () => {
      setUnitsLoading(true)
      try {
        const result = await fetchBpUnits({ bpId: formValues.buildingPassportId })
        const entries = result?.data?.data || result?.data || []
        setUnits(Array.isArray(entries) ? entries : [])
      } catch {
        setUnits([])
      } finally {
        setUnitsLoading(false)
      }
    }
    loadUnits()
    setFormValues(prev => ({ ...prev, buildingUnitPassportId: '', unitIds: [] }))
    setMultiUnitMode(false)
  }, [formValues.buildingPassportId])

  useEffect(() => {
    if (appliedPrefillRef.current.unit) return
    if (!formValues.buildingPassportId) return

    if (prefill.checklist && prefill.unitId && units.length === 0) {
      setFormValues(prev => ({
        ...prev,
        buildingUnitPassportId: prefill.unitId
      }))
      appliedPrefillRef.current.unit = true
      return
    }

    if (unitsLoading || units.length === 0) return

    if (prefill.unitIds.length > 0) {
      const validUnitIds = prefill.unitIds.filter(unitId =>
        units.some(unit => unit.id === unitId)
      )
      if (validUnitIds.length > 0) {
        setMultiUnitMode(validUnitIds.length > 1)
        setFormValues(prev => ({
          ...prev,
          buildingUnitPassportId: validUnitIds.length === 1 ? validUnitIds[0] : '',
          unitIds: validUnitIds,
          unitMode:
            validUnitIds.length > 1
              ? prefill.unitMode || 'shared'
              : 'single'
        }))
        appliedPrefillRef.current.unit = true
      }
      return
    }

    if (prefill.unitId && units.some(unit => unit.id === prefill.unitId)) {
      setFormValues(prev => ({
        ...prev,
        buildingUnitPassportId: prefill.unitId
      }))
      appliedPrefillRef.current.unit = true
    }
  }, [
    formValues.buildingPassportId,
    prefill.checklist,
    prefill.unitId,
    prefill.unitIds,
    prefill.unitMode,
    units,
    unitsLoading
  ])

  useEffect(() => {
    if (embedded) return
    if (submittedRef.current && tasks.length > prevTasksLengthRef.current) {
      submittedRef.current = false
      const rawReturn = searchParams.get('returnTo')
      const created = tasks.slice(0, tasks.length - prevTasksLengthRef.current)
      const single = created.length === 1 && created[0]?.id && !created[0]._offline ? created[0] : null
      if (rawReturn && rawReturn.startsWith('/') && !/^\/[/\\]/.test(rawReturn)) {
        redirect(rawReturn)
      } else if (single) {
        redirect(PRIVATE_PATHS.taskDetails.replace(':id', single.id), { fromCreate: true })
      } else {
        redirect(`${PRIVATE_PATHS.taskManagement}?tab=tasks`)
      }
    }
    prevTasksLengthRef.current = tasks.length
  }, [tasks])

  const handleChange = (name, value) => {
    setFormValues(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const handleTaskActionChange = taskActionType => {
    setFormValues(prev => {
      const checklistFileTypeIds = prefill.checklistFileTypeId
        ? [String(prefill.checklistFileTypeId)]
        : prev.fileTypeIds.slice(0, 1)
      const nextFileTypeIds = prefill.checklist
        ? checklistFileTypeIds
        : taskActionType === 'upload_single'
          ? prev.fileTypeIds.slice(0, 1)
          : prev.fileTypeIds

      return {
        ...prev,
        taskActionType,
        formTemplateId: taskActionType === 'form'
          ? (String(prev.formTemplateId) === String(uploadTaskTemplate?.id) ? '' : prev.formTemplateId)
          : (uploadTaskTemplate?.id || ''),
        fileTypeIds: taskActionType === 'form' && !prefill.checklist ? [] : nextFileTypeIds,
        taskType: taskActionType === 'form' ? prev.taskType : (prev.taskType || 'data_collection')
      }
    })
    setErrors(prev => ({
      ...prev,
      taskActionType: null,
      formTemplateId: null,
      fileTypeIds: null
    }))
  }

  const formRef = useRef(null)

  const validate = () => {
    const newErrors = {}
    if (!formValues.title.trim()) newErrors.title = 'Title is required'
    if (formValues.taskActionType === 'form' && !formValues.formTemplateId) {
      newErrors.formTemplateId = 'Form template is required'
    }
    if (formValues.taskActionType === 'upload_single' && formValues.fileTypeIds.length !== 1) {
      newErrors.fileTypeIds = 'Select one file type'
    }
    if (formValues.taskActionType === 'upload_multiple' && formValues.fileTypeIds.length === 0) {
      newErrors.fileTypeIds = 'Select at least one file type'
    }
    if (!formValues.buildingPassportId) newErrors.buildingPassportId = 'Building Passport is required'
    if (
      prefill.checklist &&
      formValues.assigneeIds.length === 0 &&
      formValues.inviteEmails.length === 0
    ) {
      newErrors.assigneeIds = 'Select at least one assignee for this upload task'
    }
    // file_type_id validation removed — requirement needs clarification

    // A user may be both an assignee and an approver — the approver picker
    // shows a non-blocking warning rather than failing validation.

    if (formValues.requiresApproval && formValues.approverIds.length === 0) {
      newErrors.approverIds = 'Choose at least one approver, or turn off "Approval required"'
    }

    // Recurrence validation
    if (formValues.recurrenceType === 'repeating' && !formValues.recurrenceStartDate) {
      newErrors.recurrenceStartDate = 'Start date is required for repeating tasks'
    }
    if (formValues.recurrenceType === 'repeating' && formValues.recurrenceStartDate) {
      if (formValues.recurrenceStartDate < todayLocal) {
        newErrors.recurrenceStartDate = 'Start date cannot be in the past'
      }
      if (formValues.recurrenceEndDate && formValues.recurrenceEndDate < formValues.recurrenceStartDate) {
        newErrors.recurrenceEndDate = 'End date cannot be before start date'
      }
    }

    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0]
      const el = formRef.current?.querySelector(`#${firstErrorKey}, [name="${firstErrorKey}"]`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return false
    }
    return true
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (!validate()) return

    submittedRef.current = true

    const payload = {
      title: formValues.title.trim(),
      buildingPassportId: formValues.buildingPassportId
    }

    if (formValues.formTemplateId) payload.formTemplateId = formValues.formTemplateId

    if (multiUnitMode && formValues.unitIds.length > 0) {
      payload.unitIds = formValues.unitIds
      payload.unitMode = formValues.unitIds.length > 1 ? formValues.unitMode : 'single'
    } else if (formValues.buildingUnitPassportId) {
      payload.buildingUnitPassportId = formValues.buildingUnitPassportId
    }
    if (formValues.description.trim()) payload.description = formValues.description.trim()
    if (formValues.priority) payload.priority = formValues.priority
    if (formValues.taskType) payload.taskType = formValues.taskType
    if (formValues.estimatedDurationMinutes !== '' && formValues.estimatedDurationMinutes !== null) {
      payload.estimatedDurationMinutes = Number(formValues.estimatedDurationMinutes)
    }
    if (formValues.deadline) payload.deadline = formValues.deadline
    if (formValues.taskProjectId) payload.taskProjectId = formValues.taskProjectId
    if (formValues.taskTeamId) payload.taskTeamId = formValues.taskTeamId
    if (formValues.contractorId) payload.contractorId = formValues.contractorId
    if (formValues.poNumber.trim()) payload.poNumber = formValues.poNumber.trim()
    if (formValues.taskDomainId) payload.taskDomainId = formValues.taskDomainId
    if (formValues.programmeObligationId) payload.programmeObligationId = formValues.programmeObligationId
    if (formValues.assigneeIds.length > 0) payload.assigneeIds = formValues.assigneeIds
    if (formValues.inviteEmails.length > 0) payload.inviteEmails = formValues.inviteEmails
    payload.requiresApproval = !!formValues.requiresApproval
    if (formValues.requiresApproval && formValues.approverIds.length > 0) {
      payload.approverIds = formValues.approverIds
    }
    if (formValues.requiresSignOff) {
      payload.requiresSignOff = true
    }
    if (formValues.requiresEvidence) {
      payload.requiresEvidence = true
    }
    if (formValues.taskActionType !== 'form') {
      const selectedTypes = formValues.fileTypeIds
        .map(id => fileTypes.find(type => String(type.id) === String(id)))
        .filter(Boolean)
      const primaryFileType = selectedTypes[0] || null

      payload.metadata = {
        source: prefill.checklist ? 'analytics_checklist_item' : 'task_file_upload',
        taskActionType: formValues.taskActionType,
        uploadMode: formValues.taskActionType === 'upload_multiple' ? 'multiple' : 'single',
        fileTypeId: primaryFileType?.id || formValues.fileTypeIds[0],
        fileTypeIds: formValues.fileTypeIds,
        fileTypes: selectedTypes.map(type => ({
          id: type.id,
          name: type.name,
          fileCategoryId: type.fileCategoryId || null
        })),
        fileCategoryId: primaryFileType?.fileCategoryId || prefill.checklistFileCategoryId || null,
        ...(prefill.checklist ? {
          analyticsTemplateId: prefill.checklistTemplateId,
          analyticsTemplateName: prefill.checklistTemplateName,
          analyticsTemplateItemId: prefill.checklistItemId,
          analyticsTemplateItemTitle: prefill.checklistItemTitle,
          analyticsTemplateItemGroupNo: prefill.checklistGroupNo !== ''
            ? Number(prefill.checklistGroupNo)
            : 0,
          assignmentScope: prefill.checklistAssignmentScope || (formValues.buildingUnitPassportId ? 'unit' : 'bp'),
          tagIds: prefill.checklistTagIds
        } : {})
      }
    }
    if (formValues.recurrenceType === 'repeating') {
      payload.recurrenceType = 'repeating'
      payload.recurrenceInterval = formValues.recurrenceInterval
      payload.recurrenceUnit = formValues.recurrenceUnit
      payload.recurrenceStartDate = formValues.recurrenceStartDate
      if (formValues.recurrenceEndDate)
        payload.recurrenceEndDate = formValues.recurrenceEndDate
    }

    if (prefill.followUpFor) payload.followUpForTaskId = prefill.followUpFor
    if (sourceDocumentLinked) payload.sourceFileId = prefill.sourceFileId
    if (blockOriginal && prefill.followUpFor) payload.blocksTaskId = prefill.followUpFor

    dispatch(createTaskRoutine(embedded && onCreated ? { ...payload, onSuccess: () => onCreated() } : payload))
  }

  const handleBack = () => {
    if (embedded && onCancel) { onCancel(); return }
    const rawReturn = searchParams.get('returnTo')
    if (rawReturn && rawReturn.startsWith('/') && !/^\/[/\\]/.test(rawReturn)) {
      redirect(rawReturn)
      return
    }
    if (hasInAppHistory(location)) {
      routerNavigate(-1)
      return
    }
    const parentId = prefill.followUpFor
    redirect(parentId ? PRIVATE_PATHS.taskDetails.replace(':id', parentId) : getHubReturn(`${PRIVATE_PATHS.taskManagement}?tab=tasks`))
  }

  return (
    <PageWrapper $embedded={embedded}>
      <ContentContainer $embedded={embedded}>
        {!embedded && (
          <TopBar>
            <BackButton onClick={handleBack}>
              <ArrowBackIcon />
              <span>Back to Tasks</span>
            </BackButton>
          </TopBar>
        )}

        <FormCard>
          {!embedded && (
            <FormHeader>
              <FormHeaderIcon>
                <AssignmentOutlinedIcon />
              </FormHeaderIcon>
              <FormHeaderText>
                <FormTitle>Create New Task</FormTitle>
                <FormSubtitle>Fill in the details below to create a new task.</FormSubtitle>
              </FormHeaderText>
            </FormHeader>
          )}

          <Form ref={formRef} onSubmit={handleSubmit}>
            {/* --- Basics --- */}
            <SectionDivider>
              <SectionIcon $color="#6366f1" $bg="#eef2ff">
                <EditNoteOutlinedIcon />
              </SectionIcon>
              <SectionDividerLabel>Basics</SectionDividerLabel>
            </SectionDivider>

            <SectionCard>
              <FormGroup>
                <Label htmlFor="title">
                  <LabelIcon $color="#6366f1"><EditNoteOutlinedIcon /></LabelIcon>
                  Title <Required>*</Required>
                </Label>
                <Input
                  id="title"
                  type="text"
                  placeholder="Enter task title"
                  value={formValues.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  $hasError={!!errors.title}
                  data-tour-id='taskCreate.title'
                />
                {errors.title && <ErrorText>{errors.title}</ErrorText>}
              </FormGroup>

              {prefill.sourceFileId && (
                <FollowUpBox data-testid='source-document-notice'>
                  <SourceDocText>
                    {sourceDocumentLinked
                      ? <>This task will be linked to the document <strong>{prefill.sourceFileName || 'you came from'}</strong>. The document will list this task, and the task will link back to the document.</>
                      : <>You picked a different building, so this task will not be linked to the document <strong>{prefill.sourceFileName || 'you came from'}</strong>. Pick that document's building again to keep the link.</>}
                  </SourceDocText>
                  {sourceDocumentLinked && prefill.openRemedials > 0 && (
                    <SourceDocHint>
                      This document has {prefill.openRemedials} recommended remedial{prefill.openRemedials === 1 ? '' : 's'} still waiting for a decision. If this task fixes one of them, go back to the document and use “+ Create task” on that remedial instead. That way the remedial closes by itself when the task is done, and nobody creates a second task for it.
                    </SourceDocHint>
                  )}
                </FollowUpBox>
              )}

              {prefill.followUpFor && (
                <FollowUpBox>
                  <FollowUpText>
                    This is a follow-up to <strong>{prefill.followUpTitle || 'another task'}</strong>.
                  </FollowUpText>
                  <FollowUpCheck type='button' onClick={() => setBlockOriginal(v => !v)}>
                    <FollowUpTick $on={blockOriginal}>{blockOriginal ? '✓' : ''}</FollowUpTick>
                    <span>The original task can’t be completed until this follow-up is done</span>
                  </FollowUpCheck>
                </FollowUpBox>
              )}

              <FormGroup>
                <Label htmlFor="description">
                  <LabelIcon $color="#8b5cf6"><NotesOutlinedIcon /></LabelIcon>
                  Description
                </Label>
                <TextArea
                  id="description"
                  placeholder="Add a description (optional)"
                  value={formValues.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={4}
                />
              </FormGroup>

              <FormGroup id="taskActionType">
                <Label>
                  <LabelIcon $color="#ec4899"><AssignmentOutlinedIcon /></LabelIcon>
                  What should the assignee do? <Required>*</Required>
                </Label>
                <TaskActionGrid role="radiogroup" aria-label="Task action">
                  {taskActionOptions.map(option => {
                    const ActionIcon = option.icon
                    const selected = formValues.taskActionType === option.value

                    return (
                      <TaskActionCard
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        $selected={selected}
                        onClick={() => handleTaskActionChange(option.value)}
                        data-tour-id={option.value === 'form' ? 'taskCreate.action-form' : undefined}
                      >
                        <TaskActionIcon $selected={selected}>
                          <ActionIcon />
                        </TaskActionIcon>
                        <TaskActionCopy>
                          <TaskActionTitle>{option.label}</TaskActionTitle>
                          <TaskActionDescription>{option.description}</TaskActionDescription>
                        </TaskActionCopy>
                        <TaskActionRadio $selected={selected} aria-hidden="true" />
                      </TaskActionCard>
                    )
                  })}
                </TaskActionGrid>
                {prefill.checklist && (
                  <ChecklistPrefillNote>
                    <InfoOutlinedIcon />
                    Choose what the assignee should do. For either upload option, the document type is fixed by the checklist item.
                  </ChecklistPrefillNote>
                )}
                {errors.taskActionType && <ErrorText>{errors.taskActionType}</ErrorText>}
              </FormGroup>

              {formValues.taskActionType === 'form' ? (
                <FormGroup>
                  <LabelRow>
                    <Label htmlFor="formTemplateId">
                      <LabelIcon $color="#ec4899"><DescriptionOutlinedIcon /></LabelIcon>
                      Form <Required>*</Required>
                      <Tooltip title="A form is the list of questions the person doing this work will answer, with any photos or signatures they need to add. Choose a ready-made form or create your own." arrow>
                        <InfoOutlinedIcon style={{ fontSize: 14, marginLeft: 4, color: '#999', cursor: 'help', verticalAlign: 'text-bottom' }} />
                      </Tooltip>
                    </Label>
                    <InlineLink onClick={() => redirect(PRIVATE_PATHS.formBuilder)}>
                      Manage all forms
                    </InlineLink>
                  </LabelRow>
                  <div data-tour-id='taskCreate.form-field'>
                    <FormTemplateField
                      value={formValues.formTemplateId}
                      template={selectedFormTemplate}
                      onChange={(template) => handleChange('formTemplateId', template?.id || '')}
                      buildingPassportId={formValues.buildingPassportId || undefined}
                      programmeObligationId={formValues.programmeObligationId || undefined}
                      error={errors.formTemplateId}
                    />
                  </div>
                </FormGroup>
              ) : (
                <FormGroup>
                  <Label htmlFor="fileTypeIds">
                    <LabelIcon $color="#0f9dad"><UploadFileOutlinedIcon /></LabelIcon>
                    {formValues.taskActionType === 'upload_multiple' && !prefill.checklist ? 'File Types' : 'File Type'} <Required>*</Required>
                  </Label>
                  {prefill.checklist ? (
                    <LockedFileTypeValue>
                      <UploadFileOutlinedIcon />
                      <span>
                        {selectedFileTypeOptions.map(type => type.name).join(', ') ||
                          (fileTypesLoading ? 'Loading file type...' : 'Checklist file type unavailable')}
                      </span>
                      <LockOutlinedIcon />
                    </LockedFileTypeValue>
                  ) : (
                    <PickerWrapper $hasError={!!errors.fileTypeIds}>
                      <Autocomplete
                      id="fileTypeIds"
                      multiple={formValues.taskActionType === 'upload_multiple'}
                      disableCloseOnSelect={formValues.taskActionType === 'upload_multiple'}
                      options={orderedFileTypeOptions}
                      value={formValues.taskActionType === 'upload_multiple'
                        ? selectedFileTypeOptions
                        : (selectedFileTypeOptions[0] || null)}
                      loading={fileTypesLoading}
                      isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                      getOptionLabel={option => option?.name || ''}
                      onChange={(_, value) => {
                        const selected = Array.isArray(value) ? value : (value ? [value] : [])
                        handleChange('fileTypeIds', selected.map(type => String(type.id)))
                      }}
                      renderOption={(props, option, state) => (
                        <li {...props}>
                          {formValues.taskActionType === 'upload_multiple' && (
                            <Checkbox
                              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                              checkedIcon={<CheckBoxIcon fontSize="small" />}
                              checked={state.selected}
                              sx={{ mr: 1, p: 0.5 }}
                            />
                          )}
                          <FileTypeOptionText>
                            <span>{option.name}</span>
                            {option.description && <small>{option.description}</small>}
                          </FileTypeOptionText>
                        </li>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          placeholder={fileTypesLoading
                            ? 'Loading file types...'
                            : formValues.taskActionType === 'upload_multiple'
                              ? 'Search and select file types'
                              : 'Search and select a file type'}
                          size="small"
                        />
                      )}
                      />
                    </PickerWrapper>
                  )}
                  <HelpText>
                    {prefill.checklist
                      ? (formValues.taskActionType === 'upload_multiple'
                        ? 'This document type is fixed by the checklist item. The assignee can upload multiple files of this type.'
                        : 'This document type is fixed by the checklist item and cannot be changed here.')
                      : formValues.taskActionType === 'upload_multiple'
                      ? 'Selected file types stay at the top and can be removed at any time.'
                      : 'The assignee will be directed to upload this document type.'}
                  </HelpText>
                  {errors.fileTypeIds && <ErrorText>{errors.fileTypeIds}</ErrorText>}
                </FormGroup>
              )}

              <FormGroup>
                <Label htmlFor="buildingPassportId">
                  <LabelIcon $color="#f59e0b"><ApartmentOutlinedIcon /></LabelIcon>
                  Building Passport <Required>*</Required>
                </Label>
                <div data-tour-id='taskCreate.building'>
                  <SearchableSelect
                    id="buildingPassportId"
                    value={formValues.buildingPassportId}
                    onChange={v => handleChange('buildingPassportId', v)}
                    options={buildingPassportOptions.map(bp => ({ value: bp.id, label: bp.name }))}
                    placeholder={bpsLoading ? 'Loading Building Passports...' : 'Select a Building Passport'}
                    searchPlaceholder='Search Building Passports…'
                    ariaLabel='Building Passport'
                  />
                </div>
                {errors.buildingPassportId && <ErrorText>{errors.buildingPassportId}</ErrorText>}
              </FormGroup>

              {shouldShowUnitPicker && (
                <>
                  {!multiUnitMode ? (
                    <FormGroup>
                      <LabelRow>
                        <Label htmlFor="buildingUnitPassportId">
                          <LabelIcon $color="#f59e0b"><MeetingRoomOutlinedIcon /></LabelIcon>
                          Unit (optional)
                        </Label>
                        <InlineLink onClick={() => {
                          setMultiUnitMode(true)
                          handleChange('buildingUnitPassportId', '')
                        }}>
                          Assign to multiple units
                        </InlineLink>
                      </LabelRow>
                      <SearchableSelect
                        id="buildingUnitPassportId"
                        value={formValues.buildingUnitPassportId}
                        onChange={v => handleChange('buildingUnitPassportId', v)}
                        options={unitOptions.map(unit => ({ value: unit.id, label: unit.name }))}
                        placeholder={unitsLoading && unitOptions.length === 0 ? 'Loading units...' : 'All units / No specific unit'}
                        clearable
                        clearLabel='All units / No specific unit'
                        searchPlaceholder='Search units…'
                        ariaLabel='Unit'
                      />
                    </FormGroup>
                  ) : (
                    <FormGroup>
                      <LabelRow>
                        <Label>
                          <LabelIcon $color="#f59e0b"><MeetingRoomOutlinedIcon /></LabelIcon>
                          Units
                        </Label>
                        <InlineLink onClick={() => {
                          setMultiUnitMode(false)
                          handleChange('unitIds', [])
                        }}>
                          Switch to single unit
                        </InlineLink>
                      </LabelRow>
                      <MultiUnitSelect>
                        <UnitCheckboxRow>
                          <CheckboxInput
                            type='checkbox'
                            id='unit-select-all'
                            checked={formValues.unitIds.length === units.length}
                            ref={el => {
                              if (el) el.indeterminate = formValues.unitIds.length > 0 && formValues.unitIds.length < units.length
                            }}
                            onChange={() => {
                              if (formValues.unitIds.length === units.length) {
                                handleChange('unitIds', [])
                              } else {
                                handleChange('unitIds', units.map(u => u.id))
                              }
                            }}
                          />
                          <label htmlFor='unit-select-all' style={{ fontWeight: 600 }}>Select All ({units.length})</label>
                        </UnitCheckboxRow>
                        <div style={{ borderBottom: '1px solid #e5e7eb', margin: '4px 0' }} />
                        {orderedUnits.map(unit => {
                          const selected = formValues.unitIds.includes(unit.id)
                          return (
                            <UnitCheckboxRow key={unit.id}>
                              <CheckboxInput
                                type="checkbox"
                                id={`unit-${unit.id}`}
                                checked={selected}
                                onChange={() => {
                                  const next = selected
                                    ? formValues.unitIds.filter(id => id !== unit.id)
                                    : [...formValues.unitIds, unit.id]
                                  handleChange('unitIds', next)
                                }}
                              />
                              <label htmlFor={`unit-${unit.id}`}>{unit.name}</label>
                            </UnitCheckboxRow>
                          )
                        })}
                      </MultiUnitSelect>
                      {formValues.unitIds.length > 0 && (
                        <UnitSelectedCount>{formValues.unitIds.length} unit{formValues.unitIds.length !== 1 ? 's' : ''} selected</UnitSelectedCount>
                      )}
                      {formValues.unitIds.length > 1 && (
                        <UnitModeToggle>
                          <UnitModeLabel>
                            Create one task for all separate uploads, or one task per upload:
                          </UnitModeLabel>
                          <RecurrenceRow>
                            <RecurrenceRadio>
                              <input
                                type="radio"
                                id="unit_mode_shared"
                                name="unitMode"
                                value="shared"
                                checked={formValues.unitMode === 'shared'}
                                onChange={() => handleChange('unitMode', 'shared')}
                              />
                              <label htmlFor="unit_mode_shared">One task for all selected uploads</label>
                            </RecurrenceRadio>
                            <RecurrenceRadio>
                              <input
                                type="radio"
                                id="unit_mode_fan_out"
                                name="unitMode"
                                value="fan_out"
                                checked={formValues.unitMode === 'fan_out'}
                                onChange={() => handleChange('unitMode', 'fan_out')}
                              />
                              <label htmlFor="unit_mode_fan_out">One task per upload</label>
                            </RecurrenceRadio>
                          </RecurrenceRow>
                          <HelpText>
                            {formValues.unitMode === 'shared'
                              ? 'Creates one task linked to all selected units and their required uploads.'
                              : `Creates ${formValues.unitIds.length} separate tasks, one for each selected unit upload.`}
                          </HelpText>
                        </UnitModeToggle>
                      )}
                    </FormGroup>
                  )}
                </>
              )}
            </SectionCard>

            {/* --- Assignment --- */}
            <SectionDivider>
              <SectionIcon $color="#0d9488" $bg="#e0f7f7">
                <PersonAddAltOutlinedIcon />
              </SectionIcon>
              <SectionDividerLabel>Assignment</SectionDividerLabel>
            </SectionDivider>

            <SectionCard>
              <FormGroup>
                <Label htmlFor="taskTeamId">
                  <LabelIcon $color="#0d9488"><GroupsOutlinedIcon /></LabelIcon>
                  Share with Team
                </Label>
                <SearchableSelect
                  id="taskTeamId"
                  value={formValues.taskTeamId}
                  onChange={v => handleChange('taskTeamId', v || '')}
                  options={[
                    ...teams.filter(t => !t.isSystem).map(team => ({
                      value: team.id,
                      label: `${team.name} (${team.memberCount || team.members?.length || 0} members)`,
                    })),
                    ...teams.filter(t => t.isSystem).map(team => ({
                      value: team.id,
                      label: `${team.name} (${team.memberCount || team.members?.length || 0} members)`,
                      group: 'Your organisation',
                    })),
                  ]}
                  clearable
                  clearLabel='No team'
                  placeholder='No team'
                  searchPlaceholder='Search teams…'
                  ariaLabel='Share with team'
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="contractorId">
                  <LabelIcon $color="#7C5FE0"><GroupsOutlinedIcon /></LabelIcon>
                  Contractor
                </Label>
                <SearchableSelect
                  id="contractorId"
                  value={formValues.contractorId}
                  onChange={v => handleChange('contractorId', v || '')}
                  options={availableContractors.map(c => ({
                    value: c.id,
                    label: c.name,
                    sublabel: [c.company, c.assignmentBlocked ? 'Unavailable' : (c.assignmentWarnings?.length ? 'Insurance lapsed' : null)].filter(Boolean).join(' · ') || undefined,
                    disabled: c.assignmentBlocked,
                  }))}
                  clearable
                  clearLabel='No contractor'
                  placeholder='No contractor'
                  searchPlaceholder='Search contractors…'
                  ariaLabel='Contractor'
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="poNumber">
                  <LabelIcon $color="#0e7490"><ReceiptLongOutlinedIcon /></LabelIcon>
                  Purchase order number
                </Label>
                <Input
                  id="poNumber"
                  type="text"
                  placeholder="e.g. PO-2026-001234 (optional)"
                  value={formValues.poNumber}
                  onChange={(e) => handleChange('poNumber', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="taskDomainId">
                  <LabelIcon $color="#0E7490"><CategoryOutlinedIcon /></LabelIcon>
                  Domain
                </Label>
                <SearchableSelect
                  id="taskDomainId"
                  value={formValues.taskDomainId}
                  onChange={v => handleChange('taskDomainId', v)}
                  options={availableDomains.map(d => ({ value: d.id, label: d.name }))}
                  placeholder='No domain'
                  clearable
                  clearLabel='No domain'
                  searchPlaceholder='Search domains…'
                  ariaLabel='Domain'
                />
              </FormGroup>

              {formValues.buildingPassportId && obligationOptions.length > 0 && (
                <FormGroup>
                  <Label htmlFor="programmeObligationId">
                    <LabelIcon $color="#15803d"><VerifiedUserOutlinedIcon /></LabelIcon>
                    Linked compliance obligation
                  </Label>
                  <SearchableSelect
                    id="programmeObligationId"
                    value={formValues.programmeObligationId}
                    onChange={v => handleChange('programmeObligationId', v)}
                    options={obligationOptions.map(o => ({ value: o.id, label: `${o.name}${o.domainName && o.domainName !== 'Unclassified' ? ` — ${o.domainName}` : ''}` }))}
                    placeholder='Not linked to an obligation'
                    clearable
                    clearLabel='Not linked to an obligation'
                    searchPlaceholder='Search obligations…'
                    ariaLabel='Linked compliance obligation'
                  />
                  <ApprovalHint>
                    {obligationOptions.find(o => o.id === formValues.programmeObligationId)?.kind === 'file_type'
                      ? 'This task tracks the work to get valid evidence — upload the document to satisfy the obligation.'
                      : 'Completing this task will satisfy the selected compliance obligation.'}
                  </ApprovalHint>
                </FormGroup>
              )}

              {selectedTeam && (
                <TeamInfoBanner>
                  <TeamInfoIcon>
                    <InfoOutlinedIcon />
                  </TeamInfoIcon>
                  <TeamInfoContent>
                    {internalMembers.length > 0 && (
                      <TeamMemberSection>
                        <TeamInfoText>
                          <strong>{internalMembers.length} internal member{internalMembers.length !== 1 ? 's' : ''}</strong> of <strong>{selectedTeam.name}</strong> will automatically see this task.
                        </TeamInfoText>
                        <TeamMemberPreview>
                          {internalMembers.map((m, i) => {
                            const alreadyAssigned =
                              formValues.assigneeIds.includes(m.user.id)
                            return (
                              <TeamMemberChip key={i} $added={alreadyAssigned}>
                                <span>{m.user.firstName} {m.user.lastName}</span>
                                {!alreadyAssigned ? (
                                  <TeamMemberAddBtn
                                    $internal
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); handleAddSingleInternalAssignee(m.user.id) }}
                                    title="Add as assignee"
                                  >+</TeamMemberAddBtn>
                                ) : (
                                  <TeamMemberAddedCheck>&#10003;</TeamMemberAddedCheck>
                                )}
                              </TeamMemberChip>
                            )
                          })}
                        </TeamMemberPreview>
                        <AddTeamMembersBtn type="button" onClick={handleAddInternalAsAssignees}>
                          <PersonAddAltOutlinedIcon style={{ fontSize: 15 }} />
                          Add all as assignees
                        </AddTeamMembersBtn>
                      </TeamMemberSection>
                    )}
                    {externalMembers.length > 0 && (
                      <TeamMemberSection $external>
                        <TeamInfoText $external>
                          <strong>{externalMembers.length} external member{externalMembers.length !== 1 ? 's' : ''}</strong> — assign individually for access.
                        </TeamInfoText>
                        <TeamMemberPreview>
                          {externalMembers.map((m, i) => {
                            const alreadyInvited = formValues.inviteEmails.some(item =>
                              typeof item === 'string' ? item === m.email : item.email === m.email
                            )
                            return (
                              <TeamMemberChip key={i} $external $added={alreadyInvited}>
                                <span>{m.name || maskEmail(m.email)}</span>
                                {!alreadyInvited ? (
                                  <TeamMemberAddBtn
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); handleAddSingleExternalInvite(m.email, m.name) }}
                                    title="Invite to this task"
                                  >+</TeamMemberAddBtn>
                                ) : (
                                  <TeamMemberAddedCheck>&#10003;</TeamMemberAddedCheck>
                                )}
                              </TeamMemberChip>
                            )
                          })}
                        </TeamMemberPreview>
                        {externalMembers.some(m => !formValues.inviteEmails.some(item =>
                          typeof item === 'string' ? item === m.email : item.email === m.email
                        )) && (
                          <AddTeamMembersBtn type="button" onClick={handleAddExternalAsInvites} $external>
                            <PersonAddAltOutlinedIcon style={{ fontSize: 15 }} />
                            Invite all external members
                          </AddTeamMembersBtn>
                        )}
                      </TeamMemberSection>
                    )}
                    {internalMembers.length === 0 && externalMembers.length === 0 && (
                      <TeamInfoText>
                        <strong>{selectedTeam.name}</strong> has no members yet.
                      </TeamInfoText>
                    )}
                  </TeamInfoContent>
                </TeamInfoBanner>
              )}

              <FormGroup>
                <div data-tour-id='taskCreate.assignees'>
                  <UserPicker
                    label="Assignees"
                    value={formValues.assigneeIds}
                    onChange={ids => handleChange('assigneeIds', ids)}
                    buildingPassportId={formValues.buildingPassportId}
                    placeholder="Search users to assign..."
                    showEmailInvite
                    inviteEmails={formValues.inviteEmails}
                    onInviteEmailsChange={(emails) => handleChange('inviteEmails', emails)}
                    initialUsers={prefill.assigneeUsers}
                    knownUsers={[
                      ...prefill.assigneeUsers,
                      ...internalMembers.map(m => m.user)
                    ]}
                  />
                </div>
                {errors.assigneeIds && (
                  <ErrorText>{errors.assigneeIds}</ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <ApprovalToggle>
                  <ApprovalIconBox>
                    <VerifiedUserOutlinedIcon />
                  </ApprovalIconBox>
                  <ApprovalContent>
                    <CheckboxRow>
                      <CheckboxInput
                        type="checkbox"
                        id="requiresApproval"
                        checked={formValues.requiresApproval}
                        onChange={(e) => handleChange('requiresApproval', e.target.checked)}
                      />
                      <label htmlFor="requiresApproval">
                        <ApprovalLabel>Requires approval before completion</ApprovalLabel>
                        <ApprovalHint>An approver will review the submitted form</ApprovalHint>
                      </label>
                    </CheckboxRow>
                    <CheckboxRow style={{ marginTop: 12 }}>
                      <CheckboxInput
                        type="checkbox"
                        id="requiresEvidence"
                        checked={formValues.requiresEvidence}
                        onChange={(e) => handleChange('requiresEvidence', e.target.checked)}
                      />
                      <label htmlFor="requiresEvidence">
                        <ApprovalLabel>Requires evidence before submission</ApprovalLabel>
                        <ApprovalHint>Task can't be submitted until a file or photo is attached</ApprovalHint>
                      </label>
                    </CheckboxRow>
                  </ApprovalContent>
                </ApprovalToggle>
              </FormGroup>

              {formValues.requiresApproval && (
                <FormGroup>
                  <UserPicker
                    label="Approvers"
                    value={formValues.approverIds}
                    onChange={(ids) => handleChange('approverIds', ids)}
                    buildingPassportId={formValues.buildingPassportId}
                    placeholder="Search users to set as approvers..."
                  />
                  {formValues.approverIds.some(id => formValues.assigneeIds.includes(id)) && (
                    <WarnText>
                      Heads up: the same person is set as both an assignee and an approver, so they would approve their own work. You can still save.
                    </WarnText>
                  )}
                  {errors.approverIds && <ErrorText>{errors.approverIds}</ErrorText>}
                </FormGroup>
              )}

              <FormGroup>
                <ApprovalToggle>
                  <ApprovalIconBox $color="#059669" $bg="#ecfdf5">
                    <AssignmentTurnedInOutlinedIcon />
                  </ApprovalIconBox>
                  <ApprovalContent>
                    <CheckboxRow>
                      <CheckboxInput
                        type="checkbox"
                        id="requiresSignOff"
                        checked={formValues.requiresSignOff}
                        onChange={(e) => handleChange('requiresSignOff', e.target.checked)}
                      />
                      <label htmlFor="requiresSignOff">
                        <ApprovalLabel>Requires manager sign-off after completion</ApprovalLabel>
                        <ApprovalHint>A manager must acknowledge and sign off on the completed task</ApprovalHint>
                      </label>
                    </CheckboxRow>
                  </ApprovalContent>
                </ApprovalToggle>
              </FormGroup>
            </SectionCard>

            {/* --- Details --- */}
            <SectionDivider>
              <SectionIcon $color="#ea580c" $bg="#fff7ed">
                <FlagOutlinedIcon />
              </SectionIcon>
              <SectionDividerLabel>Details</SectionDividerLabel>
            </SectionDivider>

            <SectionCard>
              <FormRow>
                <FormGroup>
                  <Label htmlFor="priority">
                    <LabelIcon $color="#ea580c"><FlagOutlinedIcon /></LabelIcon>
                    Priority
                  </Label>
                  <SearchableSelect
                    id="priority"
                    value={formValues.priority}
                    onChange={v => handleChange('priority', v)}
                    options={priorityOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                    searchable={false}
                    ariaLabel='Priority'
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="deadline">
                    <LabelIcon $color="#dc2626"><CalendarTodayOutlinedIcon /></LabelIcon>
                    Deadline
                  </Label>
                  <Input
                    id="deadline"
                    type="date"
                    value={formValues.deadline}
                    onChange={(e) => handleChange('deadline', e.target.value)}
                    data-tour-id='taskCreate.deadline'
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label htmlFor="taskType">
                    <LabelIcon $color="#0e7490"><FlagOutlinedIcon /></LabelIcon>
                    Task type
                  </Label>
                  <SearchableSelect
                    id="taskType"
                    value={formValues.taskType}
                    onChange={v => handleChange('taskType', v)}
                    options={taskTypeOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                    searchable={false}
                    ariaLabel='Task type'
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="estimatedDurationMinutes">
                    <LabelIcon $color="#7c3aed"><CalendarTodayOutlinedIcon /></LabelIcon>
                    Estimated duration (minutes)
                  </Label>
                  <Input
                    id="estimatedDurationMinutes"
                    type="number"
                    min="1"
                    max="100000"
                    placeholder="e.g. 45"
                    value={formValues.estimatedDurationMinutes}
                    onChange={(e) => handleChange('estimatedDurationMinutes', e.target.value)}
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label htmlFor="taskProjectId">
                  <LabelIcon $color="#33becc"><FolderOutlinedIcon /></LabelIcon>
                  Project
                </Label>
                <SearchableSelect
                  id="taskProjectId"
                  value={showCreateProject ? null : formValues.taskProjectId}
                  onChange={v => {
                    if (v === '__new__') {
                      setShowCreateProject(true)
                    } else {
                      handleChange('taskProjectId', v)
                      setShowCreateProject(false)
                    }
                  }}
                  options={[
                    ...projects.map(project => ({ value: project.id, label: project.name })),
                    { value: '__new__', label: '+ Create new project' },
                  ]}
                  placeholder='No project'
                  clearable
                  clearLabel='No project'
                  searchPlaceholder='Search projects…'
                  ariaLabel='Project'
                />
                {showCreateProject && (
                  <InlineProjectForm>
                    <Input
                      placeholder="Project name"
                      value={newProjectName}
                      onChange={e => { setNewProjectName(e.target.value); setCreateProjectError('') }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleCreateProject()
                        }
                      }}
                      autoFocus
                    />
                    {createProjectError && <ErrorText>{createProjectError}</ErrorText>}
                    <InlineProjectButtons>
                      <InlineProjectBtn onClick={() => { setShowCreateProject(false); setNewProjectName(''); setCreateProjectError('') }}>
                        Cancel
                      </InlineProjectBtn>
                      <InlineProjectBtnPrimary onClick={handleCreateProject} disabled={!newProjectName.trim() || creatingProject}>
                        {creatingProject ? 'Creating...' : 'Create'}
                      </InlineProjectBtnPrimary>
                    </InlineProjectButtons>
                  </InlineProjectForm>
                )}
              </FormGroup>
            </SectionCard>

            {/* --- Schedule --- */}
            <SectionDivider>
              <SectionIcon $color="#7c3aed" $bg="#f5f3ff">
                <RepeatOutlinedIcon />
              </SectionIcon>
              <SectionDividerLabel>Schedule</SectionDividerLabel>
            </SectionDivider>

            <SectionCard>
              {formValues.programmeObligationId ? (
                <ObligationScheduleNote>
                  <LockOutlinedIcon style={{ fontSize: 16 }} />
                  <span>This task follows its compliance obligation’s schedule — the compliance programme creates the next one automatically each cycle, so it can’t have its own repeat.</span>
                </ObligationScheduleNote>
              ) : (
              <>
              <FormGroup>
                <Label>
                  <LabelIcon $color="#7c3aed"><RepeatOutlinedIcon /></LabelIcon>
                  Recurrence
                </Label>
                <RecurrenceRow>
                  <RecurrenceRadio>
                    <input
                      type="radio"
                      id="recurrence_one_off"
                      name="recurrenceType"
                      value="one_off"
                      checked={formValues.recurrenceType === 'one_off'}
                      onChange={() => handleChange('recurrenceType', 'one_off')}
                    />
                    <label htmlFor="recurrence_one_off">One-off</label>
                  </RecurrenceRadio>
                  <RecurrenceRadio data-tour-id='taskCreate.recurrence-repeating'>
                    <input
                      type="radio"
                      id="recurrence_repeating"
                      name="recurrenceType"
                      value="repeating"
                      checked={formValues.recurrenceType === 'repeating'}
                      onChange={() => handleChange('recurrenceType', 'repeating')}
                    />
                    <label htmlFor="recurrence_repeating">Repeating</label>
                  </RecurrenceRadio>
                </RecurrenceRow>
              </FormGroup>

              {formValues.recurrenceType === 'repeating' && (
                <RecurrenceConfig data-tour-id='taskCreate.recurrence-config'>
                  <RecurrenceLabel>Every</RecurrenceLabel>
                  <RecurrenceIntervalInput
                    type="number"
                    min="1"
                    value={formValues.recurrenceInterval}
                    onChange={(e) => handleChange('recurrenceInterval', parseInt(e.target.value) || 1)}
                  />
                  <div style={{ flex: '0 0 auto', width: 130 }}>
                    <SearchableSelect
                      value={formValues.recurrenceUnit}
                      onChange={v => handleChange('recurrenceUnit', v)}
                      options={recurrenceUnitOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                      searchable={false}
                      ariaLabel='Recurrence unit'
                    />
                  </div>
                  {formValues.recurrenceUnit === 'week' && (
                    <>
                      <RecurrenceLabel>on</RecurrenceLabel>
                      <div style={{ flex: '0 0 auto', width: 140 }}>
                        <SearchableSelect
                          value={moment(formValues.recurrenceStartDate || todayLocal).day()}
                          onChange={v => handleChange('recurrenceStartDate', snapStartToWeekday(formValues.recurrenceStartDate, todayLocal, v))}
                          options={[1, 2, 3, 4, 5, 6, 0].map(d => ({ value: d, label: moment().day(d).format('dddd') }))}
                          ariaLabel='Day of the week'
                        />
                      </div>
                    </>
                  )}
                  {formValues.recurrenceUnit === 'month' && (
                    <>
                      <RecurrenceLabel>on the</RecurrenceLabel>
                      <div style={{ flex: '0 0 auto', width: 110 }}>
                        <SearchableSelect
                          value={moment(formValues.recurrenceStartDate || todayLocal).date()}
                          onChange={v => handleChange('recurrenceStartDate', snapStartToDayOfMonth(formValues.recurrenceStartDate, todayLocal, v))}
                          options={Array.from({ length: 31 }, (_, i) => i + 1).map(d => ({ value: d, label: moment().startOf('year').date(d).format('Do') }))}
                          ariaLabel='Day of the month'
                        />
                      </div>
                    </>
                  )}
                  <RecurrenceLabel>starting</RecurrenceLabel>
                  <Input
                    type="date"
                    value={formValues.recurrenceStartDate}
                    onChange={(e) => handleChange('recurrenceStartDate', e.target.value)}
                    $hasError={!!errors.recurrenceStartDate}
                    min={todayLocal}
                    style={{ width: 'auto' }}
                  />
                  {errors.recurrenceStartDate && <ErrorText>{errors.recurrenceStartDate}</ErrorText>}
                  <RecurrenceLabel>until</RecurrenceLabel>
                  <Input
                    type="date"
                    value={formValues.recurrenceEndDate}
                    onChange={(e) => handleChange('recurrenceEndDate', e.target.value)}
                    placeholder="No end date"
                    min={formValues.recurrenceStartDate || todayLocal}
                    style={{ width: 'auto' }}
                    $hasError={!!errors.recurrenceEndDate}
                  />
                  {errors.recurrenceEndDate && <ErrorText>{errors.recurrenceEndDate}</ErrorText>}
                </RecurrenceConfig>
              )}
              </>
              )}
            </SectionCard>

            {Object.keys(errors).length > 0 && (
              <ValidationSummary>
                Please fix the following before creating the task:
                <ul>
                  {Object.values(errors).filter(Boolean).map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              </ValidationSummary>
            )}

            <FormActions>
              <CancelButton type="button" onClick={handleBack}>
                Cancel
              </CancelButton>
              <SubmitButton type="submit" disabled={submitting} data-tour-id='taskCreate.submit'>
                {submitting ? 'Creating...' : 'Create Task'}
              </SubmitButton>
            </FormActions>
          </Form>
        </FormCard>
      </ContentContainer>
    </PageWrapper>
  )
}

export default CreateTask

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

const PageWrapper = styled.div`
  position: fixed;
  top: ${({ theme }) => theme.dimensions.topBarHeight};
  left: ${({ theme }) => theme.dimensions.navWidth};
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.backgroundColor};
  overflow-y: auto;
  animation: ${fadeIn} 0.3s ease-out;
  ${({ $embedded }) => $embedded && `
    position: static;
    background: transparent;
    overflow: visible;
    animation: none;
  `}

  @media (max-width: 768px) {
    left: 0;
    top: 60px;
    bottom: 0;
  }
`

const ContentContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 32px 24px;
  ${({ $embedded }) => $embedded && `
    max-width: none;
    padding: 0;
  `}

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`

const TopBar = styled.div`
  margin-bottom: 24px;
`

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  transition: all 0.2s;

  svg {
    font-size: 20px;
  }

  &:hover {
    background: white;
    border-color: ${({ theme }) => theme.colors.haiti};
  }
`

const FormCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 28px 32px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: linear-gradient(135deg, #fafbff 0%, #f8fffe 100%);

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const FormHeaderIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.haiti};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 24px;
    color: white;
  }
`

const FormHeaderText = styled.div`
  flex: 1;
  min-width: 0;
`

const FormTitle = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 4px 0;
`

const FormSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: 0;
`

const Form = styled.form`
  padding: 28px 32px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const FormGroup = styled.div`
  margin-bottom: 20px;
  flex: 1;
  min-width: 0;
`

const FormRow = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 6px;
`

const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`

const InlineLink = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  margin-bottom: 6px;

  &:hover {
    text-decoration: underline;
  }
`

const Required = styled.span`
  color: ${({ theme }) => theme.colors.error || theme.colors.primary};
`

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  background: ${({ theme }) => theme.colors.lightGrey};
  border: 1px solid
    ${({ $hasError, theme }) =>
      $hasError
        ? theme.colors.error || theme.colors.primary
        : theme.colors.mediumGrey};
  border-radius: 8px;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  }

  &:focus {
    background: white;
    border-color: ${({ $hasError, theme }) =>
      $hasError
        ? theme.colors.error || theme.colors.primary
        : theme.colors.secondary};
    box-shadow: 0 0 0 3px
      ${({ $hasError }) =>
        $hasError ? 'rgba(209, 73, 37, 0.1)' : 'rgba(51, 190, 204, 0.1)'};
  }
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.haiti};
  background: ${({ theme }) => theme.colors.lightGrey};
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  outline: none;
  resize: vertical;
  min-height: 80px;
  transition: all 0.2s;
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  }

  &:focus {
    background: white;
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(51, 190, 204, 0.1);
  }
`

const ErrorText = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.error || theme.colors.primary};
  margin-top: 4px;
`

const WarnText = styled.span`
  display: block;
  font-size: 12px;
  line-height: 1.4;
  color: #d97706;
  background: #fef3c7;
  border-radius: 8px;
  padding: 7px 10px;
  margin-top: 6px;
`

const HelpText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-bottom: 6px;
`

const SectionDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 32px 0 16px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};

  &:first-child {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
`

const SectionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: ${({ $bg }) => $bg || '#f1f5f9'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 18px;
    color: ${({ $color }) => $color || '#64748b'};
  }
`

const SectionDividerLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const SectionCard = styled.div`
  padding: 20px;
  background: ${({ theme }) => theme.colors.lightGrey}33;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
  margin-bottom: 4px;
`

const FollowUpBox = styled.div`
  margin-bottom: 16px;
  padding: 13px 15px;
  border: 1px solid #cfe7ea;
  background: #f2fbfb;
  border-radius: 10px;
`
const SourceDocText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: #33465a;
`
const SourceDocHint = styled.div`
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #92400e;
`
const FollowUpText = styled.div`
  font-size: 13px;
  color: #33465a;
  margin-bottom: 10px;
`
const FollowUpCheck = styled.button`
  font: inherit;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: 13px;
  color: #1c2b3a;
`
const FollowUpTick = styled.span`
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #fff;
  border: 1.5px solid ${({ $on }) => ($on ? '#2aa6b3' : '#c3ccd6')};
  background: ${({ $on }) => ($on ? '#2aa6b3' : '#fff')};
`

const LabelIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: text-bottom;
  margin-right: 5px;

  svg {
    font-size: 16px;
    color: ${({ $color }) => $color || '#64748b'};
  }
`

const TaskActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const TaskActionCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  min-height: 138px;
  padding: 14px;
  text-align: left;
  background: ${({ $selected }) => ($selected ? '#ecfeff' : '#fff')};
  border: 1px solid ${({ $selected, theme }) => ($selected ? theme.colors.secondary : theme.colors.mediumGrey)};
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.secondary};
  }


  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(51, 190, 204, 0.2);
  }
`

const TaskActionIcon = styled.span`
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: ${({ $selected }) => ($selected ? '#ccfbf1' : '#f1f5f9')};
  color: ${({ $selected, theme }) => ($selected ? theme.colors.secondary : '#64748b')};

  svg { font-size: 19px; }
`

const TaskActionCopy = styled.span`
  display: block;
  padding-right: 16px;
`

const TaskActionTitle = styled.span`
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const TaskActionDescription = styled.span`
  display: block;
  font-size: 11.5px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
`

const TaskActionRadio = styled.span`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid ${({ $selected, theme }) => ($selected ? theme.colors.secondary : theme.colors.mediumGrey)};
  background: ${({ $selected, theme }) => ($selected ? theme.colors.secondary : '#fff')};
  box-shadow: ${({ $selected }) => ($selected ? 'inset 0 0 0 3px #fff' : 'none')};
`

const ChecklistPrefillNote = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 10px;
  padding: 9px 11px;
  border-radius: 8px;
  background: #f0fdfa;
  color: #0f766e;
  font-size: 12px;

  svg { font-size: 16px; }
`

const PickerWrapper = styled.div`
  .MuiOutlinedInput-root {
    background: ${({ theme }) => theme.colors.lightGrey};
    border-radius: 8px;
    font-size: 14px;

    fieldset {
      border-color: ${({ $hasError, theme }) => $hasError
        ? theme.colors.error || theme.colors.primary
        : theme.colors.mediumGrey};
    }

    &:hover fieldset,
    &.Mui-focused fieldset {
      border-color: ${({ $hasError, theme }) => $hasError
        ? theme.colors.error || theme.colors.primary
        : theme.colors.secondary};
    }
  }
`

const LockedFileTypeValue = styled.div`
  min-height: 40px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 12px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.lightGrey};
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 14px;

  svg:first-child {
    color: ${({ theme }) => theme.colors.secondary};
    font-size: 18px;
  }

  span {
    flex: 1;
  }

  svg:last-child {
    color: #94a3b8;
    font-size: 16px;
  }
`

const FileTypeOptionText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;

  span {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.haiti};
  }

  small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #64748b;
  }
`

const ApprovalToggle = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: #f5f3ff;
  border: 1px solid #e9e5ff;
  border-radius: 10px;
`

const ApprovalIconBox = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: ${({ $bg }) => $bg || '#ede9fe'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 18px;
    color: ${({ $color }) => $color || '#7c3aed'};
  }
`

const ApprovalContent = styled.div`
  flex: 1;
  min-width: 0;
`

const CheckboxRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`

const CheckboxInput = styled.input`
  margin-top: 2px;
  accent-color: ${({ theme }) => theme.colors.primary};
  width: 16px;
  height: 16px;
  cursor: pointer;
`

const ApprovalLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
`

const ApprovalHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-top: 2px;
`

const RecurrenceRow = styled.div`
  display: flex;
  gap: 20px;
`

const RecurrenceRadio = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};

  input {
    accent-color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
  }

  label {
    cursor: pointer;
  }
`

const RecurrenceConfig = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.lightGrey};
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  margin-bottom: 20px;
`

const ObligationScheduleNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 12px 14px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13.5px;
  line-height: 1.45;
  color: #475569;
  svg { color: #94a3b8; flex-shrink: 0; margin-top: 1px; }
`

const RecurrenceLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
`

const RecurrenceIntervalInput = styled.input`
  width: 60px;
  padding: 8px 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  outline: none;
  text-align: center;
  box-sizing: border-box;

  &:focus {
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(51, 190, 204, 0.1);
  }
`

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  padding-top: 24px;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`

const CancelButton = styled.button`
  padding: 12px 24px;
  background: white;
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
    border-color: ${({ theme }) => theme.colors.haiti};
  }
`

const SubmitButton = styled.button`
  padding: 12px 32px;
  background: ${({ theme }) => theme.colors.haiti};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 4px 12px rgba(210, 71, 36, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const TeamInfoBanner = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  background: #e0f7f7;
  border: 1px solid #b2dfdb;
  border-radius: 10px;
  margin-bottom: 20px;
`

const TeamInfoIcon = styled.div`
  flex-shrink: 0;
  margin-top: 1px;

  svg {
    font-size: 20px;
    color: #0d7377;
  }
`

const TeamInfoContent = styled.div`
  flex: 1;
  min-width: 0;
`

const TeamMemberSection = styled.div`
  &:not(:first-child) {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid ${({ $external }) => $external ? '#fde68a' : '#b2dfdb'};
  }
`

const TeamInfoText = styled.div`
  font-size: 13px;
  color: ${({ $external }) => $external ? '#92400e' : '#0d7377'};
  line-height: 1.5;

  strong {
    font-weight: 600;
  }
`

const TeamMemberPreview = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
`

const TeamMemberChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  background: ${({ $added }) => $added ? '#d1fae5' : 'white'};
  border: 1px solid ${({ $external, $added }) => $added ? '#86efac' : $external ? '#fcd34d' : '#b2dfdb'};
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  color: ${({ $external }) => $external ? '#78350f' : '#1a3636'};
`

const TeamMemberAddBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid ${({ $internal }) => $internal ? '#0d7377' : '#d97706'};
  background: ${({ $internal }) => $internal ? '#e0f7f7' : '#fef3c7'};
  color: ${({ $internal }) => $internal ? '#0d7377' : '#d97706'};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  line-height: 1;
  transition: all 0.15s;

  &:hover {
    background: ${({ $internal }) => $internal ? '#0d7377' : '#d97706'};
    color: white;
  }
`

const TeamMemberAddedCheck = styled.span`
  color: #059669;
  font-size: 12px;
  font-weight: 700;
`

const TeamMemberMore = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: #0d7377;
  padding: 3px 8px;
`

const AddTeamMembersBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $external }) => $external ? '#92400e' : '#0d7377'};
  background: white;
  border: 1px solid ${({ $external }) => $external ? '#d97706' : '#0d7377'};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ $external }) => $external ? '#d97706' : '#0d7377'};
    color: white;
  }
`

const MultiUnitSelect = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.lightGrey};
  padding: 8px 12px;
`

const UnitCheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};

  label {
    cursor: pointer;
  }
`

const UnitSelectedCount = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-top: 4px;
`

const UnitModeToggle = styled.div`
  margin-top: 12px;
  padding: 12px 14px;
  background: #f5f3ff;
  border: 1px solid #e9e5ff;
  border-radius: 8px;
`

const UnitModeLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 8px;
`

const InlineProjectForm = styled.div`
  margin-top: 8px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.lightGrey};
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
`

const InlineProjectButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`

const InlineProjectBtn = styled.button`
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
    border-color: ${({ theme }) => theme.colors.haiti};
  }
`

const InlineProjectBtnPrimary = styled.button`
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  background: ${({ theme }) => theme.colors.haiti};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ValidationSummary = styled.div`
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-size: 13px;
  color: #991b1b;
  margin-bottom: 16px;

  ul {
    margin: 6px 0 0;
    padding-left: 18px;
  }

  li {
    margin-bottom: 2px;
  }
`
